import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { hasCapability, CAPABILITY } from "@/lib/capabilityPolicy";
import { api, errMsg, unwrap } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate, formatMoney } from "@/lib/utils";
import {
  ArrowLeft,
  Download,
  FileText,
  Plus,
  Trash2,
  Users as UsersIcon,
  Wallet,
  Clock,
  Pencil,
} from "lucide-react";
import { useLookupValues } from "@/lib/platformConfig";
import { displayPeriodLabel } from "@/lib/feeSelection";
import { useI18n } from "@/i18n";
import { FeePlanCard, TermBalanceCard } from "@/components/FeePlan";
import { DatePicker } from "@/components/ui/calendar";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";

const createEmptyDocForm = () => ({
  kind: "",
  number: "",
  issue_date: "",
  expiry_date: "",
  notes: "",
  file: null,
});

const DOC_LOOKUP_KEY_BY_SUBJECT_TYPE = {
  student: "student_document_kinds",
  guardian: "parent_document_kinds",
  staff: "staff_document_kinds",
};

function normalizeDocPayload(form, subjectType, subjectId) {
  const payload = {
    subject_type: subjectType,
    subject_id: subjectId,
    kind: form.kind,
  };
  if (form.number?.trim()) payload.number = form.number.trim();
  if (form.issue_date) payload.issue_date = form.issue_date;
  if (form.expiry_date) payload.expiry_date = form.expiry_date;
  if (form.notes?.trim()) payload.notes = form.notes.trim();
  return payload;
}

function SubjectDocumentsSection({
  sectionKey,
  title,
  createDialogTitle,
  subjectType,
  subjectId,
  noSubjectMessage,
  noDocsMessage,
  canReadDocs,
  canManageDocs,
}) {
  const qc = useQueryClient();
  const lookupKey = DOC_LOOKUP_KEY_BY_SUBJECT_TYPE[subjectType] || "student_document_kinds";
  const lookup = useLookupValues(lookupKey);
  const docKinds = (lookup.data?.values || []).map((row) => ({ value: row.value, label: row.label }));
  const options = docKinds;
  const docsQueryKey = ["subject-docs", subjectType, subjectId];

  const kindLabel = (kind) =>
    options.find((entry) => entry.value === kind)?.label || kind;

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createForm, setCreateForm] = useState(createEmptyDocForm);
  const [editForm, setEditForm] = useState(createEmptyDocForm);
  const [editingDoc, setEditingDoc] = useState(null);

  useEffect(() => {
    if (!options.length) return;
    setCreateForm((prev) =>
      options.some((entry) => entry.value === prev.kind)
        ? prev
        : { ...prev, kind: options[0].value },
    );
    setEditForm((prev) =>
      options.some((entry) => entry.value === prev.kind)
        ? prev
        : { ...prev, kind: options[0].value },
    );
  }, [options]);

  const { data: docs = [], isLoading: docsLoading } = useQuery({
    queryKey: docsQueryKey,
    queryFn: async () =>
      unwrap(
        await api.get("/documents", {
          params: { subject_type: subjectType, subject_id: subjectId },
        }),
      ),
    enabled: !!subjectId && canReadDocs,
  });

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const uploaded = unwrap(
      await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    );
    return uploaded.id;
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = normalizeDocPayload(createForm, subjectType, subjectId);
      if (createForm.file) payload.file_id = await uploadFile(createForm.file);
      return unwrap(await api.post("/documents", payload));
    },
    onSuccess: () => {
      toast.success("Document added");
      qc.invalidateQueries({ queryKey: docsQueryKey });
      qc.invalidateQueries({ queryKey: ["docs-expiring"] });
      setCreateForm(createEmptyDocForm());
      setCreateOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!editingDoc) return null;
      const payload = {};
      payload.kind = editForm.kind;
      payload.number = editForm.number || null;
      payload.issue_date = editForm.issue_date || null;
      payload.expiry_date = editForm.expiry_date || null;
      payload.notes = editForm.notes || null;
      if (editForm.file) payload.file_id = await uploadFile(editForm.file);
      return unwrap(await api.put(`/documents/${editingDoc.id}`, payload));
    },
    onSuccess: () => {
      toast.success("Document updated");
      qc.invalidateQueries({ queryKey: docsQueryKey });
      qc.invalidateQueries({ queryKey: ["docs-expiring"] });
      setEditOpen(false);
      setEditingDoc(null);
      setEditForm(createEmptyDocForm());
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (docId) =>
      unwrap(await api.delete(`/documents/${docId}`)),
    onSuccess: () => {
      toast.success("Document deleted");
      qc.invalidateQueries({ queryKey: docsQueryKey });
      qc.invalidateQueries({ queryKey: ["docs-expiring"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      kind: doc.kind || options[0]?.value || "",
      number: doc.number || "",
      issue_date: doc.issue_date || "",
      expiry_date: doc.expiry_date || "",
      notes: doc.notes || "",
      file: null,
    });
    setEditOpen(true);
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || `${kindLabel(doc.kind)}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Card className="border-[var(--subtle)]">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm font-display flex items-center gap-2">
          <FileText className="h-4 w-4 text-[var(--accent)]" /> {title}
        </CardTitle>
        {canManageDocs && subjectId && (
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            data-testid={`add-${sectionKey}-doc-btn`}
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!canReadDocs && (
          <div className="text-sm text-[var(--text-muted)]">
            Document details are restricted for your role.
          </div>
        )}
        {canReadDocs && !subjectId && (
          <div className="text-sm text-[var(--text-muted)]">
            {noSubjectMessage}
          </div>
        )}
        {canReadDocs && !!subjectId && docsLoading && <Skeleton className="h-24" />}
        {canReadDocs &&
          !!subjectId &&
          !docsLoading &&
          docs.length === 0 && (
            <div className="text-sm text-[var(--text-muted)]">
              {noDocsMessage}
            </div>
          )}
        {canReadDocs && !!subjectId && !docsLoading && docs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2.5">Kind</th>
                  <th className="text-left px-3 py-2.5">Number</th>
                  <th className="text-left px-3 py-2.5">Issue</th>
                  <th className="text-left px-3 py-2.5">Expiry</th>
                  <th className="text-left px-3 py-2.5">File</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => (
                  <tr key={doc.id} data-testid={`${sectionKey}-doc-${doc.id}`}>
                    <td className="px-3 py-2.5">{kindLabel(doc.kind)}</td>
                    <td className="px-3 py-2.5 font-mono-num text-xs">
                      {doc.number || "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono-num text-xs">
                      {formatDate(doc.issue_date)}
                    </td>
                    <td className="px-3 py-2.5 font-mono-num text-xs">
                      {formatDate(doc.expiry_date)}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-[var(--text-muted)]">
                      {doc.file_name || "No file"}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        {doc.file_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadDoc(doc)}
                            data-testid={`download-${sectionKey}-doc-${doc.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        {canManageDocs && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(doc)}
                              data-testid={`edit-${sectionKey}-doc-${doc.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteMut.mutate(doc.id)}
                              data-testid={`delete-${sectionKey}-doc-${doc.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {createDialogTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold">
                Document type
              </Label>
              <Select
                value={createForm.kind}
                onValueChange={(v) =>
                  setCreateForm({ ...createForm, kind: v })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Document number
              </Label>
              <Input
                value={createForm.number}
                onChange={(e) =>
                  setCreateForm({ ...createForm, number: e.target.value })
                }
                className="mt-1"
                data-testid={`${sectionKey}-create-doc-number`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Issue date
              </Label>
              <DatePicker
                value={createForm.issue_date}
                onChange={(date) =>
                  setCreateForm({ ...createForm, issue_date: date })
                }
                testId={`${sectionKey}-create-doc-issue-date`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Expiry date
              </Label>
              <DatePicker
                value={createForm.expiry_date}
                onChange={(date) =>
                  setCreateForm({ ...createForm, expiry_date: date })
                }
                testId={`${sectionKey}-create-doc-expiry-date`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Attach file
              </Label>
              <Input
                type="file"
                className="mt-1"
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    file: e.target.files?.[0] || null,
                  })
                }
                data-testid={`${sectionKey}-create-doc-file`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">Notes</Label>
              <Textarea
                value={createForm.notes}
                onChange={(e) =>
                  setCreateForm({ ...createForm, notes: e.target.value })
                }
                className="mt-1"
                data-testid={`${sectionKey}-create-doc-notes`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => createMut.mutate()}
              disabled={
                createMut.isPending || !createForm.kind || !subjectId
              }
              data-testid={`${sectionKey}-create-doc-save`}
            >
              {createMut.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              Replace or update document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold">
                Document type
              </Label>
              <Select
                value={editForm.kind}
                onValueChange={(v) =>
                  setEditForm({ ...editForm, kind: v })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!options.length && <p className="mt-1 text-xs text-[var(--text-muted)]">No document kinds configured in Platform Settings.</p>}
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Document number
              </Label>
              <Input
                value={editForm.number}
                onChange={(e) =>
                  setEditForm({ ...editForm, number: e.target.value })
                }
                className="mt-1"
                data-testid={`${sectionKey}-edit-doc-number`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Issue date
              </Label>
              <DatePicker
                value={editForm.issue_date}
                onChange={(date) =>
                  setEditForm({ ...editForm, issue_date: date })
                }
                testId={`${sectionKey}-edit-doc-issue-date`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Expiry date
              </Label>
              <DatePicker
                value={editForm.expiry_date}
                onChange={(date) =>
                  setEditForm({ ...editForm, expiry_date: date })
                }
                testId={`${sectionKey}-edit-doc-expiry-date`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Replace file
              </Label>
              <Input
                type="file"
                className="mt-1"
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    file: e.target.files?.[0] || null,
                  })
                }
                data-testid={`${sectionKey}-edit-doc-file`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) =>
                  setEditForm({ ...editForm, notes: e.target.value })
                }
                className="mt-1"
                data-testid={`${sectionKey}-edit-doc-notes`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => updateMut.mutate()}
              disabled={updateMut.isPending || !editingDoc}
              data-testid={`${sectionKey}-edit-doc-save`}
            >
              {updateMut.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StudentFamilyDocuments({ guardians, canReadDocs, canManageDocs }) {
  const guardianLinks = Array.isArray(guardians) ? guardians : [];
  const fatherGuardian = guardianLinks.find((row) => String(row?.relation || "").toLowerCase() === "father");
  const motherGuardian = guardianLinks.find((row) => String(row?.relation || "").toLowerCase() === "mother");
  const fatherGuardianId = fatherGuardian?.guardian?.id || fatherGuardian?.guardian_user_id || "";
  const motherGuardianId = motherGuardian?.guardian?.id || motherGuardian?.guardian_user_id || "";

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SubjectDocumentsSection
        sectionKey="father"
        title="Father documents"
        createDialogTitle="Add father document"
        subjectType="guardian"
        subjectId={fatherGuardianId}
        noSubjectMessage="No father guardian linked for this child."
        noDocsMessage="No documents attached for this guardian."
        canReadDocs={canReadDocs}
        canManageDocs={canManageDocs}
      />
      <SubjectDocumentsSection
        sectionKey="mother"
        title="Mother documents"
        createDialogTitle="Add mother document"
        subjectType="guardian"
        subjectId={motherGuardianId}
        noSubjectMessage="No mother guardian linked for this child."
        noDocsMessage="No documents attached for this guardian."
        canReadDocs={canReadDocs}
        canManageDocs={canManageDocs}
      />
    </div>
  );
}

export default function StudentProfile() {
  const { sid } = useParams();
  const { user } = useAuth();
  const { t, language } = useI18n();
  const levelMode = levelSystemOf(user);
  const canViewFinance = hasCapability(user, CAPABILITY.FINANCE_READ);
  const canReadDocs = hasCapability(user, CAPABILITY.DOCUMENTS_READ);
  const canManageDocs = hasCapability(user, CAPABILITY.DOCUMENTS_MANAGE);
  const canManageGuardians = hasCapability(user, CAPABILITY.STUDENTS_MANAGE);
  const [guardianOpen, setGuardianOpen] = useState(false);
  const emptyGuardianForm = {
    guardian_email: "",
    guardian_name: "",
    guardian_phone: "",
    guardian_password: "",
    relation: "father",
    is_primary: false,
    is_emergency_contact: false,
    emergency_priority: 1,
    can_view_fees: true,
    can_view_academics: true,
    can_pickup: true,
  };
  const [guardianForm, setGuardianForm] = useState(emptyGuardianForm);
  const [activatingGuardian, setActivatingGuardian] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const qc = useQueryClient();
  const {
    data: student,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["student", sid],
    queryFn: async () => unwrap(await api.get(`/students/${sid}`)),
  });
  const { data: invoices } = useQuery({
    queryKey: ["student-invoices", sid],
    queryFn: async () =>
      unwrap(await api.get("/invoices", { params: { student_id: sid } })),
    enabled: !!sid && canViewFinance,
  });
  const { data: ledger } = useQuery({
    queryKey: ["student-ledger", sid],
    queryFn: async () => unwrap(await api.get(`/students/${sid}/ledger`)),
    enabled: !!sid && canViewFinance,
  });
  const { data: timeline } = useQuery({
    queryKey: ["student-timeline", sid],
    queryFn: async () => unwrap(await api.get(`/students/${sid}/timeline`)),
    enabled: !!sid,
  });
  const { data: health } = useQuery({
    queryKey: ["student-health", sid],
    queryFn: async () => unwrap(await api.get(`/students/${sid}/health`)),
    enabled: !!sid,
  });
  const { data: guardianDocs = [] } = useQuery({
    queryKey: ["guardian-docs", sid],
    queryFn: async () => unwrap(await api.get("/documents", { params: { subject_type: "guardian" } })),
    enabled: !!sid && canManageGuardians,
  });
  const guardianMut = useMutation({
    mutationFn: async () => unwrap(await api.post(`/students/${sid}/guardians`, { ...guardianForm, guardian_password: guardianForm.guardian_password || undefined })),
    onSuccess: () => { toast.success("Guardian linked"); setGuardianOpen(false); setGuardianForm(emptyGuardianForm); qc.invalidateQueries({ queryKey: ["student", sid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const activateGuardianMut = useMutation({
    mutationFn: async () => unwrap(await api.post(`/guardians/${activatingGuardian.guardian.id}/activate`, { temporary_password: temporaryPassword })),
    onSuccess: () => {
      toast.success(t("guardianActivation.success"));
      setActivatingGuardian(null);
      setTemporaryPassword("");
      qc.invalidateQueries({ queryKey: ["student", sid] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (isLoading)
    return (
      <div className="p-8">
        <Skeleton className="h-96" />
      </div>
    );
  if (isError || !student) {
    return (
      <div className="p-6 lg:p-8 space-y-4" data-testid="student-profile-error">
        <Link
          to="/students"
          className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--accent)]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to students
        </Link>
        <Card className="border-[var(--subtle)]">
          <CardContent className="py-10 text-center">
            <div className="font-semibold text-[var(--text-primary)]">
              Could not load student profile
            </div>
            <div className="text-sm text-[var(--text-muted)] mt-1">
              {errMsg(error)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const dues = (invoices || []).reduce((a, i) => a + (i.balance_paise || 0), 0);
  const currentEnrollment = (student.enrollments || []).find((enrollment) => enrollment.status === "active");
  const enrolledYearId = currentEnrollment?.academic_year_id;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="student-profile">
      <Link
        to="/students"
        className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--accent)]"
      >
        <ArrowLeft className="h-4 w-4" /> Back to students
      </Link>

      {/* Header */}
      <div className="flex items-start gap-6">
        {student.photo_url ? (
          <img
            src={student.photo_url}
            alt={`${student.first_name} ${student.last_name}`.trim()}
            className="h-24 w-24 rounded-md object-cover border border-[var(--subtle)] flex-shrink-0"
          />
        ) : (
          <div className="h-24 w-24 rounded-md bg-[var(--brand)] text-white flex items-center justify-center font-display font-bold text-4xl flex-shrink-0">
            {student.first_name?.[0]}
          </div>
        )}
        <div className="flex-1">
          <div className="text-xs font-mono-num text-[var(--text-muted)]">
            {student.admission_no}
          </div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)] mt-1">
            {student.first_name} {student.middle_name} {student.last_name}
          </h1>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-[var(--text-secondary)]">
            <span>DOB {formatDate(student.date_of_birth)}</span>
            <span data-testid="student-profile-level">
              {levelWord(levelMode, language)}: {levelDisplayName(currentEnrollment?.grade, language)}
            </span>
            <span data-testid="student-profile-section">
              Section: {currentEnrollment?.section?.name || "-"}
            </span>
            <span className="capitalize">{student.gender}</span>
            {student.blood_group && <span>Blood: {student.blood_group}</span>}
            <span>Previous school: {student.previous_school || "-"}</span>
            <span>
              Siblings:{" "}
              {(student.sibling_student_ids || []).length
                ? student.sibling_student_ids.join(", ")
                : "-"}
            </span>
            <Badge className="bg-[var(--success-bg)] text-[var(--success)] border-0 capitalize">
              {student.status}
            </Badge>
          </div>
        </div>
        <Card className="border-[var(--subtle)] bg-[var(--brand)] text-white p-4">
          <div className="text-[10px] uppercase font-semibold tracking-widest opacity-70">
            Outstanding
          </div>
          <div className="font-mono-num text-2xl font-bold mt-1">
            {canViewFinance ? formatMoney(dues) : "--"}
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Guardians */}
        <Card className="border-[var(--subtle)]">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-[var(--accent)]" /> Guardians
            </CardTitle>
            {canManageGuardians && (
              <Button
                size="sm"
                onClick={() => setGuardianOpen(true)}
                data-testid="add-guardian-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {(student.guardians || []).map((g) => (
              <div
                key={g.id}
                className="border-l-2 border-[var(--accent)] pl-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm">
                    {g.guardian?.full_name}
                  </div>
                  <Badge
                    className={
                      g.guardian?.status === "active"
                        ? "bg-green-50 text-green-700"
                        : "bg-amber-50 text-amber-700"
                    }
                  >
                    {g.guardian?.status === "active"
                      ? t("guardianActivation.active")
                      : t("guardianActivation.inactive")}
                  </Badge>
                </div>
                <div className="text-xs text-[var(--text-muted)] capitalize">
                  {g.relation} · {g.is_primary ? "Primary" : "Secondary"}
                  {g.is_emergency_contact
                    ? ` · Emergency${g.emergency_priority ? ` (P${g.emergency_priority})` : ""}`
                    : ""}
                </div>
                <div className="text-xs font-mono-num">{g.guardian?.phone}</div>
                <div className="text-xs font-mono-num text-[var(--text-muted)]">
                  {g.guardian?.email}
                </div>
                <div className="mt-1 text-xs text-[var(--text-muted)]">
                  {t("guardianActivation.documents", {
                    count: guardianDocs.filter(
                      (doc) => doc.subject_id === g.guardian?.id
                    ).length,
                  })}
                </div>
                {canManageGuardians && g.guardian?.status !== "active" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() => setActivatingGuardian(g)}
                    data-testid={`activate-guardian-${g.guardian?.id}`}
                  >
                    {t("guardianActivation.activate")}
                  </Button>
                )}
              </div>
            ))}
            {(health?.profile?.emergency_contact_name ||
              health?.profile?.emergency_contact_phone) && (
              <div className="pt-2 mt-2 border-t border-[var(--subtle)] text-xs text-[var(--text-secondary)]">
                <div className="font-semibold text-[var(--text-primary)]">
                  Health emergency contact
                </div>
                <div>{health?.profile?.emergency_contact_name || "-"}</div>
                <div className="font-mono-num">
                  {health?.profile?.emergency_contact_phone || "-"}
                </div>
              </div>
            )}
            {(student.guardians || []).length === 0 && (
              <div className="text-sm text-[var(--text-muted)]">
                No guardians linked yet.
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={guardianOpen} onOpenChange={setGuardianOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Add or link guardian</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Email</Label><Input type="email" required value={guardianForm.guardian_email} onChange={(e) => setGuardianForm({ ...guardianForm, guardian_email: e.target.value })} data-testid="guardian-email" /></div>
              <div><Label>Full name</Label><Input required value={guardianForm.guardian_name} onChange={(e) => setGuardianForm({ ...guardianForm, guardian_name: e.target.value })} data-testid="guardian-name" /></div>
              <div><Label>Phone</Label><Input required placeholder="+971..." value={guardianForm.guardian_phone} onChange={(e) => setGuardianForm({ ...guardianForm, guardian_phone: e.target.value })} data-testid="guardian-phone" /></div>
              <div><Label>Temporary password <span className="font-normal text-[var(--text-muted)]">(new account only)</span></Label><Input type="password" value={guardianForm.guardian_password} onChange={(e) => setGuardianForm({ ...guardianForm, guardian_password: e.target.value })} data-testid="guardian-password" /><p className="text-xs text-[var(--text-muted)] mt-1">Required only if this email is not already a guardian account.</p></div>
              <div><Label>Relationship</Label><Input value={guardianForm.relation} onChange={(e) => setGuardianForm({ ...guardianForm, relation: e.target.value })} data-testid="guardian-relation" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={guardianForm.is_primary} onChange={(e) => setGuardianForm({ ...guardianForm, is_primary: e.target.checked })} /> Primary contact</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={guardianForm.is_emergency_contact} onChange={(e) => setGuardianForm({ ...guardianForm, is_emergency_contact: e.target.checked })} /> Emergency contact</label>
              {guardianForm.is_emergency_contact && <div><Label>Emergency priority (1–5)</Label><Input type="number" min="1" max="5" value={guardianForm.emergency_priority} onChange={(e) => setGuardianForm({ ...guardianForm, emergency_priority: Number(e.target.value) })} data-testid="guardian-emergency-priority" /></div>}
              <div className="border-t border-[var(--subtle)] pt-3 space-y-2"><div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Access permissions</div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={guardianForm.can_view_academics} onChange={(e) => setGuardianForm({ ...guardianForm, can_view_academics: e.target.checked })} /> View academic updates</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={guardianForm.can_view_fees} onChange={(e) => setGuardianForm({ ...guardianForm, can_view_fees: e.target.checked })} /> View fees</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={guardianForm.can_pickup} onChange={(e) => setGuardianForm({ ...guardianForm, can_pickup: e.target.checked })} /> Authorise pickup</label>
              </div>
            </div>
            <DialogFooter><Button onClick={() => guardianMut.mutate()} disabled={!guardianForm.guardian_email || !guardianForm.guardian_name || !guardianForm.guardian_phone || guardianMut.isPending} data-testid="save-guardian" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">{guardianMut.isPending ? "Saving…" : "Save guardian"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!activatingGuardian} onOpenChange={(open) => { if (!open) { setActivatingGuardian(null); setTemporaryPassword(""); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">{t("guardianActivation.title")}</DialogTitle></DialogHeader>
            <p className="text-sm text-[var(--text-muted)]">{t("guardianActivation.description", { name: activatingGuardian?.guardian?.full_name || "" })}</p>
            <div><Label>{t("guardianActivation.password")}</Label><Input type="password" value={temporaryPassword} onChange={(event) => setTemporaryPassword(event.target.value)} data-testid="guardian-activation-password" /></div>
            <DialogFooter><Button variant="outline" onClick={() => setActivatingGuardian(null)}>{t("common.cancel")}</Button><Button onClick={() => activateGuardianMut.mutate()} disabled={temporaryPassword.length < 8 || activateGuardianMut.isPending} data-testid="guardian-activation-confirm">{activateGuardianMut.isPending ? t("common.saving") : t("guardianActivation.activate")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoices */}
        <Card className="border-[var(--subtle)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[var(--accent)]" /> Invoices
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {canViewFinance ? (
              <>
                {(invoices || []).slice(0, 5).map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between text-sm border-b border-[var(--subtle)] pb-2"
                  >
                    <div>
                      <div className="font-mono-num text-xs">
                        {inv.invoice_no}
                      </div>
                      <div className="text-xs text-[var(--text-muted)]">
                        {displayPeriodLabel(inv.period_label, language)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-num font-semibold">
                        {formatMoney(inv.balance_paise)}
                      </div>
                      <div className="text-[10px] uppercase text-[var(--text-muted)]">
                        {inv.status.replace("_", " ")}
                      </div>
                    </div>
                  </div>
                ))}
                {(invoices || []).length === 0 && (
                  <div className="text-sm text-[var(--text-muted)]">
                    No invoices.
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-[var(--text-muted)]">
                Finance details are restricted for your role.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="border-[var(--subtle)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--accent)]" /> Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(timeline || []).map((t, i) => (
              <div
                key={i}
                className="text-sm border-l-2 border-[var(--subtle)] pl-3"
              >
                <div className="text-xs font-mono-num text-[var(--text-muted)]">
                  {formatDate(t.at)}
                </div>
                <div className="text-sm">{t.text}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <SubjectDocumentsSection
        sectionKey="student"
        title="Student documents"
        createDialogTitle="Add child document"
        subjectType="student"
        subjectId={sid}
        noSubjectMessage="Student profile is unavailable."
        noDocsMessage="No documents attached for this child."
        canReadDocs={canReadDocs}
        canManageDocs={canManageDocs}
      />
      <StudentFamilyDocuments
        guardians={student.guardians}
        canReadDocs={canReadDocs}
        canManageDocs={canManageDocs}
      />

      {/* Term balance and fee plan */}
      {canViewFinance && enrolledYearId && (
        <>
          <TermBalanceCard studentId={sid} academicYearId={enrolledYearId} />
          <FeePlanCard
            studentId={sid}
            academicYearId={enrolledYearId}
            canEdit={hasCapability(user, CAPABILITY.FINANCE_MANAGE)}
          />
        </>
      )}

      {/* Ledger */}
      {canViewFinance && (
        <Card className="border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display">Fee ledger</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Description</th>
                  <th className="text-right px-4 py-2.5">Debit</th>
                  <th className="text-right px-4 py-2.5">Credit</th>
                  <th className="text-right px-4 py-2.5">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(ledger || []).map((e) => (
                  <tr key={e.id}>
                    <td className="px-4 py-2.5 font-mono-num text-xs">
                      {formatDate(e.occurred_at)}
                    </td>
                    <td className="px-4 py-2.5">{e.narration}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">
                      {e.debit_paise ? formatMoney(e.debit_paise) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-[var(--success)]">
                      {e.credit_paise ? formatMoney(e.credit_paise) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">
                      {formatMoney(e.running_balance_paise)}
                    </td>
                  </tr>
                ))}
                {(ledger || []).length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-6 text-center text-[var(--text-muted)]"
                    >
                      No ledger entries.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
